import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { AppState, store } from '../store';
import { clearFcmToken } from '../store/slices/FcmSlice';
import { deleteToken, onMessage } from 'firebase/messaging';
import { messaging } from '../config/firebase';
import { request } from '../Utils/request';
import { getPersistentDeviceId } from '../Utils/deviceId';

const useFirebaseMessaging = () => {
  const dispatch = useDispatch();
  const isLoggedIn = useSelector((state: AppState) => state.auth.isLoggedIn);

  useEffect(() => {
    console.log('[FCM] init effect fired, isLoggedIn =', isLoggedIn);
    if (!isLoggedIn) {
      console.log('[FCM] init effect: user logged out, skipping');
      return; // do nothing while logged out
    }
    if (!messaging) {
      console.log('[FCM] init effect: messaging not available, skipping');
      return; // messaging not available
    }
    if (!('serviceWorker' in navigator)) {
      console.log('[FCM] init effect: service workers unsupported, skipping');
      return;
    }

    const unsubscribe = onMessage(messaging, (payload) => {
      console.log('[FCM] Foreground message1:', payload);
      if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
        const { title = 'Notification', body = '', icon } = (payload.notification as any) || {};
        new Notification(title, { body, icon: icon || '/assets/images/ifindsLogo.png' });
        window.dispatchEvent(
          new CustomEvent('app-toast', {
            detail: {
              avatar: '/assets/images/ifindsLogo.png',
              title: title,
              message: body,
              type: 'info',
              onClick: () => console.log('toast clicked'),
            },
          }),
        );
      } else if (!(window as any).__notificationMsgShown) {
        (window as any).__notificationMsgShown = true;
        window.dispatchEvent(
          new CustomEvent('app-toast', {
            detail: {
              message: 'Enable notifications from browser settings',
              type: 'warning',
              onClick: () => window.open('https://support.google.com/chrome/answer/3220216', '_blank'),
            },
          }),
        );
      }
    });

    navigator.serviceWorker.register('/firebase-messaging-sw.js').catch((err) => console.error('[FCM] SW registration error:', err));

    return () => {
      console.log('[FCM] init effect cleanup: removing foreground listener');
      unsubscribe();
    };
  }, [isLoggedIn]);

  // Clear token on logout
  useEffect(() => {
    console.log('[FCM] logout effect triggered, isLoggedIn =', isLoggedIn);
    if (isLoggedIn) {
      console.log('[FCM] logout effect: user still logged in, skipping cleanup');
      return;
    }
    if (!messaging) {
      console.log('[FCM] logout effect: messaging not available, skipping cleanup');
      return; // messaging not available
    }

    (async () => {
      try {
        const currentTokenDeleted = await deleteToken(messaging);

        if (currentTokenDeleted) {
          console.info('[FCM] Token deleted locally');
        }
      } catch (err) {
        console.error('[FCM] Failed to delete token', err);
      } finally {
        console.log('[FCM] Dispatching clearFcmToken()');
        dispatch(clearFcmToken());
      }

      // Optionally inform backend
      const deviceId = getPersistentDeviceId();
      const currentState = store.getState();
      const authToken = currentState?.auth?.token;
      console.log('[FCM] logout effect: auth token present?', !!authToken, 'deviceId:', deviceId);
      if (!authToken) {
        console.log('[FCM] logout effect: no auth token, skipping /profile/fcm/remove-token request');
        return;
      }

      try {
        await request<unknown, { deviceId: string }>('/profile/fcm/remove-token', {
          method: 'POST',
          authorization: true,
          body: {
            deviceId: deviceId,
          },
        }).then((res: any) => {
          console.info('[FCM] Token removed on backend', res);
        }).catch((err: any) => {
          console.error('[FCM] Failed to remove token on backend', err);
        });
        console.info('[FCM] Token removed on backend');
      } catch (err) {
        console.error('[FCM] Failed to remove token on backend', err);
      }
    })();
  }, [isLoggedIn, dispatch]);
};

export default useFirebaseMessaging; 